import React, { lazy } from 'react';

import { RouteConfig, Roles } from '../../routing/child-router-factory';

import {
  pathJoin,
  nonAuthenticatedRedirect,
  CreateConfigFn,
  Role,
} from '../../routing/utils';

import { RequireCapability } from '../shared/RequireCapability';

// Path maps come from the pure `route-map` modules so this file (which is in
// the eager routing graph via app/route-map) never drags the page components
// in. The section shells themselves are lazy — each ships as its own chunk
// behind the Suspense boundary in src/components/main/index.tsx.
import { createRoutesMap as createSelfServiceMap } from './routes/self-service/route-map';

import { createRoutesMap as createContainersRouteMap } from './routes/containers/route-map';

import { createRoutesMap as createCollectionsRouteMap } from './routes/collections/route-map';

import { createRoutesMap as createAccountMap } from './routes/account/route-map';

import { createRoutesMap as createMessagesMap } from './routes/messages/route-map';

import { createRoutesMap as createReportsMap } from './routes/reports/route-map';

import { createRoutesMap as createNotificationsMap } from './routes/notifications/route-map';

const SelfService = lazy(() => import('./routes/self-service/SelfService'));

const Containers = lazy(() => import('./routes/containers/Containers'));

const Collections = lazy(() => import('./routes/collections/Collections'));

const Products = lazy(() => import('./routes/products/Products'));

const Account = lazy(() => import('./routes/account/Account'));

const Services = lazy(() => import('./routes/services/Services'));

const Messages = lazy(() => import('./routes/messages/Messages'));

const Reports = lazy(() => import('./routes/reports/Reports'));

const Notifications = lazy(
  () => import('./routes/notifications/Notifications')
);

const Composting = lazy(() => import('./routes/composting/Composting'));

type Keys =
  | `self-service`
  | `containers`
  | `collections`
  | `finance`
  | `messages`
  | `products`
  | `account`
  | `services`
  | `reports`
  | `notifications`
  | `composting`;

export const createRouteMap = (prefix: string) => ({
  selfService: {
    _: pathJoin(prefix)(`self-service`).value,
    ...createSelfServiceMap(pathJoin(prefix)(`self-service`).value.slice(1)),
  },
  containers: {
    _: pathJoin(prefix)(`containers`).value,
    ...createContainersRouteMap(pathJoin(prefix)(`containers`).value.slice(1)),
  },
  collections: {
    _: pathJoin(prefix)(`collections`).value,
    ...createCollectionsRouteMap(
      pathJoin(prefix)(`collections`).value.slice(1)
    ),
  },
  messages: {
    _: pathJoin(prefix)(`messages`).value,
    ...createMessagesMap(pathJoin(prefix)(`messages`).value.slice(1)),
  },
  products: {
    _: pathJoin(prefix)(`products`).value,
  },
  account: {
    _: pathJoin(prefix)(`account`).value,
    ...createAccountMap(pathJoin(prefix)(`account`).value.slice(1)),
  },
  services: {
    _: pathJoin(prefix)(`services`).value,
  },
  reports: {
    _: pathJoin(prefix)(`reports`).value,
    ...createReportsMap(pathJoin(prefix)(`reports`).value.slice(1)),
  },
  notifications: {
    _: pathJoin(prefix)(`notifications`).value,
    ...createNotificationsMap(pathJoin(prefix)(`notifications`).value.slice(1)),
  },
  // Single page (pick an address -> fill the template -> file it), so no
  // child-path map — same shape as `products`.
  composting: {
    _: pathJoin(prefix)(`composting`).value,
  },
});

export type RouteMap = ReturnType<typeof createRouteMap>;

const authenticatedUser: Roles = {
  and: ['authenticated'],
};

export const createConfig: CreateConfigFn<Keys> = (map) => {
  const arr: (Omit<RouteConfig<Keys>, 'fallback' | `roles`> & {
    roles?: Roles;
  })[] = [
    {
      key: 'self-service',
      path: map.app.selfService._,
      children: <SelfService />,
    },
    {
      key: 'containers',
      path: map.app.containers._,
      children: <Containers />,
    },
    {
      key: 'collections',
      path: map.app.collections._,
      children: <Collections />,
    },
    {
      key: 'messages',
      path: map.app.messages._,
      children: <Messages />,
    },
    {
      key: 'products',
      path: map.app.products._,
      children: <Products />,
    },
    {
      key: 'account',
      path: map.app.account._,
      children: <Account />,
    },
    {
      key: 'services',
      path: map.app.services._,
      children: <Services />,
    },
    {
      key: 'reports',
      path: map.app.reports._,
      children: (
        <RequireCapability right="view_reports">
          <Reports />
        </RequireCapability>
      ),
    },
    {
      key: 'notifications',
      path: map.app.notifications._,
      children: <Notifications />,
    },
    // Nav visibility is gated on `crm_enable_composting`; the ROUTE is
    // not, so Composting redirects on a direct URL hit when the flag is off.
    {
      key: 'composting',
      path: map.app.composting._,
      children: <Composting />,
    },
  ];

  return [
    ...arr.map((c) => ({
      roles: authenticatedUser,
      fallback: (roles: Role[]) => nonAuthenticatedRedirect(map)(roles),
      ...c,
    })),
  ];
};
